import { Tooltip, Typography, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowRight, FaInfoCircle } from 'react-icons/fa';
import { styled } from '@mui/material/styles';
import { ChevronRight } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { SearchModes } from '../../constants';

import { setFlightTripSearchParam } from '../../store/slices/tripSearch';
import flightSearchInputSelector from '../../helpers/selectors/flightSearchInput';
import { resetTrips } from '../../store/slices/trip';
import { flightSearch, updateFlightSearchObj } from '../../store/slices/flight';
import { checkDoEnableSelectFlightsBySegment } from '../../store/slices/user';
import ChangeFlightButton from './ChangeFlightButton';
import OneWayFlightDetails from '../TravelOption/FlightInformation/FlightDetails/OneWayFlightDetails';
import checkDoEnableTripConfigDecoupling from '../../helpers/selectors/checkDoEnableTripConfigDecoupling';

const Label = styled(Typography)(({ theme, active }) => ({
  color: active ? theme.palette.text.primary : theme.palette.grey[700],
  fontWeight: active ? 600 : 400,
  textDecoration: 'none',
  fontSize: '1rem',
}));

const FlightLabel = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  '&:hover': {
    '& .info-icon': {
      opacity: 1,
    },
  },
}));

const InfoIcon = styled(FaInfoCircle)(() => ({
  fontSize: '20px',
  color: 'primary.main',
  opacity: 0.6,
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 1,
  },
}));

const CustomBreadcrumb = ({ tripId }) => {
  const dispatch = useDispatch();
  const { selectedSearchMode } = useSelector((store) => store.tripSearch);
  const doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const isTripSearch = selectedSearchMode === SearchModes.FLIGHT;
  const {
    searchObj,
    selectedFlightSegments,
    breadCrumbsIndex: activeIndex,
  } = useSelector(flightSearchInputSelector(tripId, isTripSearch));
  const { flightSegments } = searchObj || {};

  const options = useMemo(
    () =>
      flightSegments.map((segment, index) => {
        let { origin, destination } = segment;
        if (!origin && !destination && index === 1 && flightSegments[0]) {
          origin = flightSegments[0].destination;
          destination = flightSegments[0].origin;
        }

        const itinerary = selectedFlightSegments[index];
        if (!itinerary) {
          if (destination?.city) {
            return `Choose Flight to ${destination.city}`;
          }
          return `Choose Flight ${index + 1}`;
        }

        if (itinerary) {
          return (
            <span
              key={`${itinerary.carrier}-${itinerary.originGeoname?.city}-${itinerary.destinationGeoname?.city}`}
            >
              {itinerary.carrier} · {itinerary.originGeoname.city}{' '}
              <FaArrowRight className="inline text-xs mx-1" /> {itinerary.destinationGeoname.city}
            </span>
          );
        }

        return index === 0 ? 'Choose departing flight' : 'Choose returning flight';
      }),
    [flightSegments, selectedFlightSegments],
  );

  if (!doEnableSelectFlightsBySegment) return <></>;
  const handleSearch = (index) => {
    dispatch(resetTrips({ doEnableTripConfigDecoupling }));
    dispatch(
      flightSearch({
        tripId,
        isTripSearch: selectedSearchMode === SearchModes.FLIGHT,
        searchObj: {
          breadCrumbsIndex: index,
        },
      }),
    );
  };

  const handleChangeFlightClick = (index) => {
    const updatedSegments = { ...selectedFlightSegments };
    Object.keys(updatedSegments).forEach((i) => {
      if (i >= index) delete updatedSegments[i];
    });
    if (isTripSearch) {
      dispatch(
        setFlightTripSearchParam({
          selectedFlightSegments: updatedSegments,
          breadCrumbsIndex: index,
          page: 1,
        }),
      );
    } else {
      dispatch(
        updateFlightSearchObj({
          searchObj: {
            breadCrumbsIndex: index,
            selectedFlightSegments: updatedSegments,
            page: 1,
          },
          tripId,
        }),
      );
    }

    handleSearch(index);
  };
  return (
    <div className="flex flex-row p-2 pt-0">
      {options.map((label, index) => {
        const itinerary = selectedFlightSegments[index];
        const tooltipContent = itinerary && (
          <Box
            className="modal-safe-click-zone text-sm leading-5 font-body"
            sx={{
              p: 2,
              backgroundColor: '#fff',
              borderRadius: 4,
              boxShadow: 1,
              overflowWrap: 'break-word',
              color: '#000',
            }}
          >
            <OneWayFlightDetails
              tripId={tripId}
              flights={itinerary.flights}
              doShowPrice={false}
              doShowCompactView
            />
            <br />
            <ChangeFlightButton
              onClick={() => handleChangeFlightClick(index)}
              visible={!!itinerary}
              className="text-sm mt-4"
            />
          </Box>
        );

        const labelWithTooltip = itinerary ? (
          <Tooltip
            title={tooltipContent}
            placement="bottom-start"
            enterDelay={100}
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
            }}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  padding: 0,
                  margin: 0,
                  mt: '0 !important',
                  mb: '0 !important',
                  fontFamily: 'var(--fontFamilyBody)',
                  maxWidth: 'none',
                  '& *': {
                    fontWeight: 'normal !important',
                  },
                },
              },
            }}
          >
            <FlightLabel>
              <Label active={index === activeIndex ? 1 : 0}>{label}</Label>
              <InfoIcon className="info-icon" />
            </FlightLabel>
          </Tooltip>
        ) : (
          <Label active={index === activeIndex ? 1 : 0}>{label}</Label>
        );

        return index < options.length - 1
          ? [
              <div key={`${index.toString()}-div1`} className="flex flex-col items-start">
                {labelWithTooltip}
                <ChangeFlightButton
                  onClick={() => handleChangeFlightClick(index)}
                  visible={!!itinerary}
                />
              </div>,
              <div
                key={`${index.toString()}-div2`}
                className="flex flex-col items-center mx-3"
                style={{
                  marginTop: '2px',
                }}
              >
                <ChevronRight style={{ fontSize: '20px' }} />
              </div>,
            ]
          : [
              <div key={`${index.toString()}-div1`} className="flex flex-col items-start">
                {labelWithTooltip}
                <ChangeFlightButton
                  onClick={() => handleChangeFlightClick(index)}
                  visible={!!itinerary}
                />
              </div>,
            ];
      })}
    </div>
  );
};

CustomBreadcrumb.propTypes = {
  tripId: PropTypes.string,
};

CustomBreadcrumb.defaultProps = {
  tripId: undefined,
};
export default CustomBreadcrumb;
